import React from 'react';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { ReverbLogoCircleIcon, WarningIcon, BankIcon } from '@reverbdotcom/cadence/icons/react';
import PaypalLogo from '@reverbdotcom/cadence/images/vendor/paypal-logo.svg';
import { RCIcon, RCImage } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import {
  PaymentMethodQuery,
  core_apimessages_DirectCheckoutProfile_PayoutMethod,
  core_apimessages_MyShopOnboardingResponse_PaymentMethod,
} from '@reverbdotcom/commons/src/gql/graphql';

import { Paths } from '../../../components/shared/url_helpers';

const COMPONENT_MAP = {
  [core_apimessages_DirectCheckoutProfile_PayoutMethod.REVERB]: PayoutMethodMissing,
  [core_apimessages_DirectCheckoutProfile_PayoutMethod.ACH]: PayoutMethodACH,
  [core_apimessages_DirectCheckoutProfile_PayoutMethod.WALLET]: PayoutMethodWallet,
  [core_apimessages_MyShopOnboardingResponse_PaymentMethod.PAYPAL]: PayoutMethodPaypal,
};

export function PayoutMethodMissing() {
  return (
    <div className="payout-method--missing">
      <div className="d-flex fx-dir-row fx-align-center">
        <div className="icon-background">
          <RCIcon icon={<WarningIcon />} size="7" />
        </div>
        <div>
          <I18N
            html
            text="earnings.payouts.payoutMethod.missing"
          >
            <CoreLink to={Paths.shopPoliciesEdit.expand({})}>
              <I18N text="earnings.payouts.payoutMethod.bankInformation" />
            </CoreLink>
          </I18N>
        </div>
      </div>
    </div>
  );
}

export interface PayoutMethodACHProps {
  accountNumberLastFour: string;
}

export function PayoutMethodACH({ accountNumberLastFour }: PayoutMethodACHProps) {
  return (
    <div className="payout-method--ach">
      <div className="d-flex fx-dir-row mb-3">
        <div className="icon-background">
          <RCIcon icon={<BankIcon />} size="7" />
        </div>
        <div>
          <I18N
            html
            text="earnings.payouts.payoutMethod.achHtml"
            args={{
              bankAccountNumber: accountNumberLastFour,
              strongOpen: '<strong>',
              strongClose: '</strong>',
            }}
          />
        </div>
      </div>
      <CoreLink to={Paths.shopPoliciesEdit.expand({})} >
        {I18n.t('earnings.payouts.payoutMethod.managePayoutSettings')}
      </CoreLink>
    </div>
  );
}

export function PayoutMethodWallet() {
  return (
    <div className="payout-method--wallet">
      <div className="d-flex fx-dir-row mobile-fx-align-center mb-3">
        <ReverbLogoCircleIcon />
        <div>
          <I18N
            html
            text="earnings.payouts.payoutMethod.walletHtml"
            args={{
              strongOpen: '<strong>',
              strongClose: '</strong>',
            }}
          />
        </div>
      </div>
      <CoreLink to={Paths.shopPoliciesEdit.expand({})} >
        {I18n.t('earnings.payouts.payoutMethod.editHowYouGetPaid')}
      </CoreLink>
    </div>
  );
}

export function PayoutMethodPaypal() {
  return (
    <div className="payout-method--paypal">
      <div className="d-flex fx-dir-row fx-align-center mb-4">
        <RCImage
          src={PaypalLogo}
          alt="Paypal"
          imageBox="none"
        />
        <div>
          <I18N
            html
            text="earnings.payouts.payoutMethod.paypalHtml"
            args={{
              strongOpen: '<strong>',
              strongClose: '</strong>',
            }}
          />
        </div>
      </div>
      <CoreLink to={Paths.shopPoliciesEdit.expand({})} >
        {I18n.t('earnings.payouts.payoutMethod.managePayoutSettings')}
      </CoreLink>
    </div>
  );
}

interface IProps {
  payoutMethodEnum?: core_apimessages_DirectCheckoutProfile_PayoutMethod;
  accountNumberLastFour?: string;
}

export default function PayoutMethod({
  payoutMethodEnum,
  accountNumberLastFour,
}: IProps) {
  const { data, loading } = useQuery<PaymentMethodQuery>(PAYMENT_METHOD_QUERY);
  const { paymentMethod } = data?.me?.shop || {};
  const paymentMethodPaypal = paymentMethod === core_apimessages_MyShopOnboardingResponse_PaymentMethod.PAYPAL;
  const resolvedPayoutMethod = paymentMethodPaypal ? paymentMethod : payoutMethodEnum;
  const PayoutMethodTypeComponent = COMPONENT_MAP[resolvedPayoutMethod] || PayoutMethodMissing;

  if (loading) {
    return (
      <div className="earnings-payouts__overview__payout-method earnings-payouts__overview__payout-method--loading"></div>
    );
  }

  return (
    <div className="earnings-payouts__overview__payout-method">
      <PayoutMethodTypeComponent accountNumberLastFour={accountNumberLastFour} />
    </div>
  );
}

export const PAYMENT_METHOD_QUERY = gql(`
  query PaymentMethod {
    me {
      _id
      shop {
        _id
        paymentMethod
      }
    }
  }
`);
